// src/components/MissedQuestionsReview/MissedQuestionsReview.jsx
//
// Post-submission "Review Missed Questions" on the quiz result. Collapsed by
// default so the coin result stays the focus; the review is fetched from the
// server only when opened (the client never holds an answer key).
import { useState } from 'react';
import { getQuizAttemptReview } from '../../services/quizReviewService';
import ReportQuizQuestionModal, { MISSED_REVIEW_REPORT_REASONS } from '../ReportQuizQuestionModal/ReportQuizQuestionModal';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import styles from './MissedQuestionsReview.module.css';

export default function MissedQuestionsReview({ attemptId, score, totalQuestions, mediaType, tmdbId, seasonNumber, tierResult }) {
    const [expanded, setExpanded] = useState(false);
    const [status, setStatus] = useState('idle'); // idle | loading | ready | error
    const [review, setReview] = useState(null);
    const [reporting, setReporting] = useState(null);
    const [reported, setReported] = useState({});

    const total = Number.isInteger(totalQuestions) ? totalQuestions : null;
    const missedCount = total !== null && Number.isInteger(score) ? Math.max(0, total - score) : null;

    if (missedCount === 0) {
        return <p className={styles.perfect}>Perfect score — nothing to review.</p>;
    }
    if (!attemptId || missedCount === null) return null;

    const analyticsBase = { media_type: mediaType || 'unknown', media_id: tmdbId ?? undefined, season_number: seasonNumber ?? undefined };

    async function load() {
        setStatus('loading');
        try {
            const data = await getQuizAttemptReview(attemptId);
            setReview(data);
            setStatus('ready');
        } catch {
            setStatus('error');
        }
    }

    function toggle() {
        const next = !expanded;
        setExpanded(next);
        if (!next) return;
        trackEvent(ANALYTICS_EVENTS.QUIZ_REVIEW_OPENED, {
            ...analyticsBase, score, missed_count: missedCount, tier_result: tierResult || 'none',
        }, { onceKey: attemptId });
        if (status === 'idle' || status === 'error') load();
    }

    function startReport(item) {
        trackEvent(ANALYTICS_EVENTS.QUIZ_MISSED_QUESTION_REPORT_STARTED, { ...analyticsBase, question_id: item.questionId });
        setReporting(item);
    }

    function handleReported(item, { reason, duplicate }) {
        trackEvent(ANALYTICS_EVENTS.QUIZ_MISSED_QUESTION_REPORT_SUBMITTED, { ...analyticsBase, question_id: item.questionId, reason, duplicate });
        setReported((prev) => ({ ...prev, [item.questionId]: true }));
    }

    const panelId = `missed-review-${attemptId}`;
    const items = review?.missedQuestions ?? [];

    return (
        <section className={styles.section} aria-labelledby={`${panelId}-title`}>
            <h2 id={`${panelId}-title`} className={styles.title}>Review missed questions</h2>
            <p className={styles.summary}>
                You scored {score}/{total} · {missedCount} {missedCount === 1 ? 'question' : 'questions'} missed
            </p>
            <button
                type="button"
                className={styles.toggle}
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={toggle}
            >
                {expanded ? 'Hide review' : 'Review missed questions'}
            </button>

            {expanded && (
                <div id={panelId} className={styles.panel}>
                    {status === 'loading' && <p className={styles.state} role="status">Loading your missed questions…</p>}

                    {status === 'error' && (
                        <div className={styles.state} role="alert">
                            <p>We couldn't load your review. Check your connection and try again.</p>
                            <button type="button" className={styles.retry} onClick={load}>Try again</button>
                        </div>
                    )}

                    {status === 'ready' && review?.available === false && (
                        <p className={styles.state}>Review isn't available for this challenge.</p>
                    )}

                    {status === 'ready' && review?.available !== false && (
                        <>
                            <ol className={styles.list}>
                                {items.map((item, i) => (
                                    <li key={item.questionId} className={styles.card}>
                                        <p className={styles.cardIndex}>Missed question {i + 1} of {items.length}</p>
                                        <p className={styles.question}>{item.questionText}</p>

                                        <div className={`${styles.answer} ${styles.incorrect}`}>
                                            <span className={styles.icon} aria-hidden="true">✕</span>
                                            <div className={styles.answerBody}>
                                                <span className={styles.answerLabel}>Incorrect — Your answer</span>
                                                <span className={styles.answerText}>{item.userAnswer?.text ?? 'No answer'}</span>
                                            </div>
                                        </div>

                                        <div className={`${styles.answer} ${styles.correct}`}>
                                            <span className={styles.icon} aria-hidden="true">✓</span>
                                            <div className={styles.answerBody}>
                                                <span className={styles.answerLabel}>Correct answer</span>
                                                <span className={styles.answerText}>{item.correctAnswer.text}</span>
                                            </div>
                                        </div>

                                        {item.updatedSinceAttempt && (
                                            <p className={styles.note}>This question was updated after your challenge.</p>
                                        )}

                                        <button
                                            type="button"
                                            className={styles.report}
                                            onClick={() => startReport(item)}
                                            disabled={!!reported[item.questionId]}
                                            aria-label={reported[item.questionId] ? `Question ${i + 1} reported` : `Report question ${i + 1}`}
                                        >
                                            {reported[item.questionId] ? 'Reported ✓' : '🚩 Report question'}
                                        </button>
                                    </li>
                                ))}
                            </ol>
                            {review?.unavailableCount > 0 && (
                                <p className={styles.state}>
                                    {review.unavailableCount === 1
                                        ? '1 missed question is no longer available to review.'
                                        : `${review.unavailableCount} missed questions are no longer available to review.`}
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}

            <ReportQuizQuestionModal
                isOpen={!!reporting}
                onClose={() => setReporting(null)}
                quizAttemptId={attemptId}
                questionId={reporting?.questionId}
                questionText={reporting?.questionText}
                reasons={MISSED_REVIEW_REPORT_REASONS}
                answerContext={reporting ? { userAnswerText: reporting.userAnswer?.text ?? null, correctAnswerText: reporting.correctAnswer.text } : undefined}
                metadata={{ source: 'missed_review' }}
                onSubmitted={(outcome) => reporting && handleReported(reporting, outcome)}
            />
        </section>
    );
}
